import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box, Typography, TextField, Button, IconButton, Chip,
  Dialog, DialogTitle, DialogContent, DialogActions,
  FormControl, InputLabel, Select, MenuItem, Paper,
  Divider, Rating, CircularProgress, Alert, Tooltip,
  Accordion, AccordionSummary, AccordionDetails,
  Tabs, Tab, Autocomplete, Collapse, Menu,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import PlaceIcon from '@mui/icons-material/Place';
import HotelIcon from '@mui/icons-material/Hotel';
import FlightTakeoffIcon from '@mui/icons-material/FlightTakeoff';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import HikingIcon from '@mui/icons-material/Hiking';
import NoteIcon from '@mui/icons-material/Note';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import MapIcon from '@mui/icons-material/Map';
import ListAltIcon from '@mui/icons-material/ListAlt';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import CloseIcon from '@mui/icons-material/Close';
import ScheduleIcon from '@mui/icons-material/Schedule';
import RefreshIcon from '@mui/icons-material/Refresh';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { useLoadScript, GoogleMap, MarkerF, InfoWindowF, PolylineF } from '@react-google-maps/api';
import { GOOGLE_MAPS_API_KEY } from '../../config';
import { getStorage, ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { auth } from '../../utils/firebase';

// ─── Constants ───────────────────────────────────────────────────────────────

const LIBRARIES = ['places'];

const DAY_COLORS = [
  '#ef5350', '#42a5f5', '#66bb6a', '#ffa726', '#ab47bc',
  '#26c6da', '#ec407a', '#8d6e63', '#78909c', '#26a69a',
];

const TYPE_META = {
  activity: { label: 'Activity',    color: '#42a5f5', Icon: HikingIcon },
  food:     { label: 'Food & Drink', color: '#ffa726', Icon: RestaurantIcon },
  stay:     { label: 'Stay',         color: '#66bb6a', Icon: HotelIcon },
  other:    { label: 'Other',        color: '#9e9e9e', Icon: NoteIcon },
};

const CURRENCIES = ['SGD', 'USD', 'EUR', 'GBP', 'JPY', 'AUD', 'THB', 'IDR', 'MYR', 'HKD'];

const uid = () => `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;

const dayColor = (n) => DAY_COLORS[(n - 1) % DAY_COLORS.length];

const pinSvg = (color, label) =>
  `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="30" height="38" viewBox="0 0 30 38">
      <path d="M15 0C6.72 0 0 6.72 0 15c0 11.25 15 23 15 23S30 26.25 30 15C30 6.72 23.28 0 15 0z" fill="${color}" stroke="white" stroke-width="1.5"/>
      <circle cx="15" cy="15" r="9" fill="white"/>
      <text x="15" y="19.5" text-anchor="middle" font-size="9" font-weight="bold" font-family="Arial,sans-serif" fill="${color}">${label}</text>
    </svg>`
  )}`;

// ─── Small helpers ────────────────────────────────────────────────────────────

const TypeChip = ({ type, size = 'small' }) => {
  const m = TYPE_META[type] || TYPE_META.other;
  return (
    <Chip
      label={m.label}
      size={size}
      sx={{ bgcolor: `${m.color}22`, color: m.color, border: `1px solid ${m.color}44`, fontWeight: 500, fontSize: '0.7rem' }}
    />
  );
};

const TypeIcon = ({ type, size = 18 }) => {
  const m = TYPE_META[type] || TYPE_META.other;
  const I = m.Icon;
  return <I sx={{ fontSize: size, color: m.color }} />;
};

// ─── AddSegmentDialog ─────────────────────────────────────────────────────────

const AddSegmentDialog = ({ open, onClose, onSave, existing }) => {
  const [name, setName] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [flightDetails, setFlightDetails] = useState('');
  const [hotelDetails, setHotelDetails] = useState('');
  const [notes, setNotes] = useState('');

  useEffect(() => {
    if (open) {
      setName(existing?.name || '');
      setStartDate(existing?.startDate || '');
      setEndDate(existing?.endDate || '');
      setFlightDetails(existing?.flightDetails || '');
      setHotelDetails(existing?.hotelDetails || '');
      setNotes(existing?.notes || '');
    }
  }, [open, existing]);

  const handleSave = () => {
    if (!name.trim()) return;
    onSave({ name: name.trim(), startDate, endDate, flightDetails, hotelDetails, notes });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{existing ? 'Edit Segment' : 'Add Trip Segment'}</DialogTitle>
      <DialogContent>
        <TextField label="Location / Area" fullWidth value={name} onChange={e => setName(e.target.value)}
          placeholder="e.g. Canggu, Seminyak, Tokyo" sx={{ mb: 2, mt: 1 }} required />
        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          <TextField label="Start Date" type="date" fullWidth value={startDate}
            onChange={e => setStartDate(e.target.value)} InputLabelProps={{ shrink: true }} />
          <TextField label="End Date" type="date" fullWidth value={endDate}
            onChange={e => setEndDate(e.target.value)} InputLabelProps={{ shrink: true }} />
        </Box>
        <TextField label="Flight details" fullWidth value={flightDetails}
          onChange={e => setFlightDetails(e.target.value)} placeholder="e.g. SQ123 arrives 10am"
          InputProps={{ startAdornment: <FlightTakeoffIcon sx={{ mr: 1, color: 'text.secondary' }} /> }}
          sx={{ mb: 2 }} />
        <TextField label="Hotel / Accommodation" fullWidth value={hotelDetails}
          onChange={e => setHotelDetails(e.target.value)} placeholder="e.g. The Layar, Seminyak"
          InputProps={{ startAdornment: <HotelIcon sx={{ mr: 1, color: 'text.secondary' }} /> }}
          sx={{ mb: 2 }} />
        <TextField label="Notes" fullWidth multiline rows={2} value={notes}
          onChange={e => setNotes(e.target.value)} placeholder="Any other details..." />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" disabled={!name.trim()}>
          {existing ? 'Save' : 'Add'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ─── AddItemDialog ─────────────────────────────────────────────────────────────

const AddItemDialog = ({ open, onClose, onSave, existing, autocompleteService, placesService }) => {
  const [text, setText] = useState('');
  const [type, setType] = useState('activity');
  const [searchQuery, setSearchQuery] = useState('');
  const [predictions, setPredictions] = useState([]);
  const [resolvedPlace, setResolvedPlace] = useState(null); // { placeName, placeId, address, location }
  const [loadingPlace, setLoadingPlace] = useState(false);
  const sessionToken = useRef(null);

  useEffect(() => {
    if (open) {
      setText(existing?.text || '');
      setType(existing?.type || 'activity');
      setSearchQuery(existing?.placeName ? existing.placeName : '');
      setResolvedPlace(existing?.placeName ? {
        placeName: existing.placeName,
        placeId: existing.placeId,
        address: existing.address,
        location: existing.location,
      } : null);
      setPredictions([]);
      if (window.google) {
        sessionToken.current = new window.google.maps.places.AutocompleteSessionToken();
      }
    }
  }, [open, existing]);

  const handleSearch = useCallback((query) => {
    setSearchQuery(query);
    setResolvedPlace(null);
    if (!query.trim() || !autocompleteService.current) { setPredictions([]); return; }
    autocompleteService.current.getPlacePredictions(
      { input: query, sessionToken: sessionToken.current },
      (results, status) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK) {
          setPredictions(results || []);
        } else {
          setPredictions([]);
        }
      }
    );
  }, [autocompleteService]);

  const handleSelectPrediction = (prediction) => {
    if (!prediction || !placesService.current) return;
    setLoadingPlace(true);
    placesService.current.getDetails(
      { placeId: prediction.place_id, fields: ['name', 'formatted_address', 'geometry', 'place_id'], sessionToken: sessionToken.current },
      (place, status) => {
        setLoadingPlace(false);
        if (status === window.google.maps.places.PlacesServiceStatus.OK && place) {
          setResolvedPlace({
            placeName: place.name,
            placeId: place.place_id,
            address: place.formatted_address,
            location: { lat: place.geometry.location.lat(), lng: place.geometry.location.lng() },
          });
          setSearchQuery(place.name);
          sessionToken.current = new window.google.maps.places.AutocompleteSessionToken();
        }
        setPredictions([]);
      }
    );
  };

  const handleSave = () => {
    if (!text.trim()) return;
    onSave({
      text: text.trim(),
      type,
      ...(resolvedPlace || { placeName: null, placeId: null, address: null, location: null }),
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{existing ? 'Edit Item' : 'Add Brainstorm Item'}</DialogTitle>
      <DialogContent>
        <TextField label="What do you want to do?" fullWidth value={text}
          onChange={e => setText(e.target.value)} placeholder="e.g. Surf at Echo Beach, try babi guling..."
          sx={{ mb: 2, mt: 1 }} />
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel>Type</InputLabel>
          <Select value={type} onChange={e => setType(e.target.value)} label="Type">
            {Object.entries(TYPE_META).map(([k, v]) => (
              <MenuItem key={k} value={k}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <v.Icon sx={{ fontSize: 18, color: v.color }} /> {v.label}
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {autocompleteService.current && (
          <>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
              Optional: pin to a Google Place (for map)
            </Typography>
            <Autocomplete
              freeSolo
              options={predictions}
              getOptionLabel={p => typeof p === 'string' ? p : p.description}
              inputValue={searchQuery}
              onInputChange={(_, v, reason) => { if (reason !== 'reset') handleSearch(v); }}
              onChange={(_, v) => { if (v && typeof v !== 'string') handleSelectPrediction(v); }}
              renderInput={(params) => (
                <TextField {...params} label="Search Google Places" placeholder="Search for a specific place..."
                  InputProps={{ ...params.InputProps, startAdornment: <PlaceIcon sx={{ mr: 1, color: 'text.secondary' }} /> }}
                />
              )}
              renderOption={(props, option) => (
                <li {...props} key={option.place_id}>
                  <Box>
                    <Typography variant="body2">{option.structured_formatting?.main_text}</Typography>
                    <Typography variant="caption" color="text.secondary">{option.structured_formatting?.secondary_text}</Typography>
                  </Box>
                </li>
              )}
            />
            {loadingPlace && <CircularProgress size={16} sx={{ mt: 1 }} />}
            {resolvedPlace && (
              <Alert severity="success" icon={<PlaceIcon />} sx={{ mt: 1, py: 0 }}>
                <Typography variant="caption">{resolvedPlace.placeName} — {resolvedPlace.address}</Typography>
              </Alert>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" disabled={!text.trim()}>
          {existing ? 'Save' : 'Add'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ─── PlanView ─────────────────────────────────────────────────────────────────

const PlanView = ({
  list, segments, selectedSegmentId, onSelectSegment, onUpdateSegments,
  onGenerateItinerary, generating, autocompleteService, placesService,
}) => {
  const [addSegOpen, setAddSegOpen] = useState(false);
  const [editSeg, setEditSeg] = useState(null);
  const [addItemOpen, setAddItemOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiSuggestions, setAiSuggestions] = useState([]);
  const [aiError, setAiError] = useState('');
  const [segMenuAnchor, setSegMenuAnchor] = useState(null);
  const [segMenuTarget, setSegMenuTarget] = useState(null);
  const [itemMenuAnchor, setItemMenuAnchor] = useState(null);
  const [itemMenuTarget, setItemMenuTarget] = useState(null);
  const [infoOpen, setInfoOpen] = useState(true);

  const selectedSeg = segments.find(s => s.id === selectedSegmentId);

  // ── Segment CRUD ──
  const handleAddSegment = (data) => {
    const newSeg = { id: uid(), brainstorm: [], ...data };
    const updated = [...segments, newSeg];
    onUpdateSegments(updated);
    onSelectSegment(newSeg.id);
    setAddSegOpen(false);
  };

  const handleEditSegment = (data) => {
    const updated = segments.map(s => s.id === editSeg.id ? { ...s, ...data } : s);
    onUpdateSegments(updated);
    setEditSeg(null);
  };

  const handleDeleteSegment = (id) => {
    if (!window.confirm('Delete this segment and all its brainstorm items?')) return;
    const updated = segments.filter(s => s.id !== id);
    onUpdateSegments(updated);
    if (selectedSegmentId === id) onSelectSegment(updated[0]?.id || null);
    setSegMenuAnchor(null);
  };

  // ── Brainstorm item CRUD ──
  const updateBrainstorm = (segId, newBrainstorm) => {
    const updated = segments.map(s => s.id === segId ? { ...s, brainstorm: newBrainstorm } : s);
    onUpdateSegments(updated);
  };

  const handleAddItem = (data) => {
    const item = { id: uid(), ...data };
    updateBrainstorm(selectedSegmentId, [...(selectedSeg.brainstorm || []), item]);
    setAddItemOpen(false);
  };

  const handleEditItem = (data) => {
    const newBrainstorm = selectedSeg.brainstorm.map(i => i.id === editItem.id ? { ...i, ...data } : i);
    updateBrainstorm(selectedSegmentId, newBrainstorm);
    setEditItem(null);
  };

  const handleDeleteItem = (itemId) => {
    const newBrainstorm = selectedSeg.brainstorm.filter(i => i.id !== itemId);
    updateBrainstorm(selectedSegmentId, newBrainstorm);
    setItemMenuAnchor(null);
  };

  // ── AI suggestions ──
  const fetchSuggestions = async () => {
    if (!selectedSeg) return;
    setAiLoading(true); setAiError(''); setAiSuggestions([]);
    try {
      const res = await fetch('/api/ai/suggest', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ segmentName: selectedSeg.name, tripName: list.name, brainstormItems: selectedSeg.brainstorm || [] }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed');
      setAiSuggestions(data.suggestions || []);
    } catch (e) {
      setAiError(e.message);
    } finally {
      setAiLoading(false);
    }
  };

  const addSuggestion = (sug) => {
    const item = { id: uid(), ...sug, placeName: null, placeId: null, address: null, location: null };
    updateBrainstorm(selectedSegmentId, [...(selectedSeg.brainstorm || []), item]);
    setAiSuggestions(prev => prev.filter(s => s !== sug));
  };

  const totalItems = segments.reduce((acc, s) => acc + (s.brainstorm?.length || 0), 0);

  return (
    <Box>
      {/* Mobile: horizontal chip row for segment selection */}
      <Box sx={{ display: { xs: 'flex', sm: 'none' }, gap: 1, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        {segments.map(seg => (
          <Chip
            key={seg.id}
            label={seg.name}
            onClick={() => onSelectSegment(seg.id)}
            onDelete={segments.length > 1 ? () => { setSegMenuTarget(seg); handleDeleteSegment(seg.id); } : undefined}
            sx={{
              bgcolor: selectedSegmentId === seg.id ? 'primary.dark' : 'rgba(255,255,255,0.06)',
              color: selectedSegmentId === seg.id ? 'primary.light' : 'text.primary',
              border: '1px solid',
              borderColor: selectedSegmentId === seg.id ? 'primary.main' : 'transparent',
            }}
          />
        ))}
        <Chip icon={<AddIcon />} label="Add" variant="outlined" onClick={() => setAddSegOpen(true)} size="small" />
      </Box>

    <Box sx={{ display: 'flex', gap: 2 }}>
      {/* Left: Segment list (desktop only) */}
      <Paper elevation={0} sx={{ display: { xs: 'none', sm: 'flex' }, width: 200, flexShrink: 0, bgcolor: 'rgba(255,255,255,0.04)', borderRadius: 2, p: 1, flexDirection: 'column', alignSelf: 'flex-start' }}>
        <Typography variant="caption" sx={{ px: 1, py: 0.5, color: 'text.secondary', fontWeight: 600, letterSpacing: 1 }}>
          SEGMENTS
        </Typography>
        <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
          {segments.map(seg => (
            <Box key={seg.id} sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
              <Box onClick={() => onSelectSegment(seg.id)} sx={{
                flexGrow: 1, p: 1.5, borderRadius: 1, cursor: 'pointer',
                bgcolor: selectedSegmentId === seg.id ? 'primary.dark' : 'transparent',
                '&:hover': { bgcolor: selectedSegmentId === seg.id ? 'primary.dark' : 'action.hover' },
              }}>
                <Typography variant="body2" fontWeight={selectedSegmentId === seg.id ? 600 : 400} noWrap>
                  {seg.name}
                </Typography>
                {seg.startDate && (
                  <Typography variant="caption" color="text.secondary" display="block">
                    {seg.startDate}{seg.endDate && ` → ${seg.endDate}`}
                  </Typography>
                )}
                <Typography variant="caption" color="text.secondary">
                  {seg.brainstorm?.length || 0} items
                </Typography>
              </Box>
              <IconButton size="small" onClick={e => { setSegMenuAnchor(e.currentTarget); setSegMenuTarget(seg); }}>
                <MoreVertIcon fontSize="small" />
              </IconButton>
            </Box>
          ))}
        </Box>
        <Button size="small" startIcon={<AddIcon />} onClick={() => setAddSegOpen(true)} fullWidth sx={{ mt: 1 }}>
          Add Segment
        </Button>
      </Paper>

      {/* Right: Brainstorm panel */}
      <Box sx={{ flexGrow: 1 }}>
        {!selectedSeg ? (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography color="text.secondary">Select or add a segment to start brainstorming</Typography>
          </Box>
        ) : (
          <>
            {/* Segment info (collapsible) */}
            <Accordion expanded={infoOpen} onChange={(_, v) => setInfoOpen(v)} elevation={0}
              sx={{ bgcolor: 'rgba(255,255,255,0.04)', borderRadius: '8px !important', mb: 2, '&:before': { display: 'none' } }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle1" fontWeight={600}>{selectedSeg.name}</Typography>
                {selectedSeg.startDate && (
                  <Typography variant="body2" color="text.secondary" sx={{ ml: 2, alignSelf: 'center' }}>
                    {selectedSeg.startDate} → {selectedSeg.endDate || '?'}
                  </Typography>
                )}
              </AccordionSummary>
              <AccordionDetails>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {selectedSeg.flightDetails && (
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                      <FlightTakeoffIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                      <Typography variant="body2">{selectedSeg.flightDetails}</Typography>
                    </Box>
                  )}
                  {selectedSeg.hotelDetails && (
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                      <HotelIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                      <Typography variant="body2">{selectedSeg.hotelDetails}</Typography>
                    </Box>
                  )}
                  {selectedSeg.notes && (
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                      <NoteIcon sx={{ fontSize: 16, color: 'text.secondary', mt: 0.3 }} />
                      <Typography variant="body2">{selectedSeg.notes}</Typography>
                    </Box>
                  )}
                  {!selectedSeg.flightDetails && !selectedSeg.hotelDetails && !selectedSeg.notes && (
                    <Typography variant="body2" color="text.secondary">No details yet.</Typography>
                  )}
                  <Button size="small" startIcon={<EditIcon />} onClick={() => setEditSeg(selectedSeg)} sx={{ alignSelf: 'flex-start', mt: 0.5 }}>
                    Edit Details
                  </Button>
                </Box>
              </AccordionDetails>
            </Accordion>

            {/* Brainstorm items */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle2" color="text.secondary" fontWeight={600}>
                BRAINSTORM ({selectedSeg.brainstorm?.length || 0} ideas)
              </Typography>
              <Button size="small" startIcon={<AddIcon />} variant="outlined" onClick={() => { setEditItem(null); setAddItemOpen(true); }}>
                Add Idea
              </Button>
            </Box>

            {(!selectedSeg.brainstorm || selectedSeg.brainstorm.length === 0) ? (
              <Paper elevation={0} sx={{ p: 3, textAlign: 'center', bgcolor: 'rgba(255,255,255,0.02)', borderRadius: 2, border: '1px dashed rgba(255,255,255,0.1)', mb: 2 }}>
                <Typography color="text.secondary" variant="body2">No ideas yet. Add things to do, eat, or see — or let AI suggest!</Typography>
              </Paper>
            ) : (
              <Box sx={{ mb: 2 }}>
                {selectedSeg.brainstorm.map((item) => (
                  <Paper key={item.id} elevation={0}
                    sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1.5, mb: 1, bgcolor: 'rgba(255,255,255,0.04)', borderRadius: 2, '&:hover': { bgcolor: 'rgba(255,255,255,0.07)' } }}>
                    <TypeIcon type={item.type} />
                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                      <Typography variant="body2" noWrap>{item.text}</Typography>
                      {item.placeName && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                          <PlaceIcon sx={{ fontSize: 11 }} />{item.placeName}
                        </Typography>
                      )}
                    </Box>
                    <TypeChip type={item.type} />
                    <IconButton size="small" onClick={e => { setItemMenuAnchor(e.currentTarget); setItemMenuTarget(item); }}>
                      <MoreVertIcon fontSize="small" />
                    </IconButton>
                  </Paper>
                ))}
              </Box>
            )}

            {/* AI suggestions */}
            <Divider sx={{ my: 2 }} />
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1 }}>
              <Button size="small" variant="outlined" startIcon={aiLoading ? <CircularProgress size={14} /> : <AutoAwesomeIcon />}
                onClick={fetchSuggestions} disabled={aiLoading} sx={{ borderColor: 'primary.main' }}>
                {aiLoading ? 'Thinking…' : 'AI Suggest'}
              </Button>
              <Typography variant="caption" color="text.secondary">Get ideas for {selectedSeg.name}</Typography>
            </Box>
            {aiError && <Alert severity="error" sx={{ mb: 1, py: 0 }}>{aiError}</Alert>}
            {aiSuggestions.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                  Click to add to brainstorm:
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {aiSuggestions.map((sug, i) => (
                    <Chip key={i} label={sug.text} size="small" onClick={() => addSuggestion(sug)}
                      icon={<AddIcon />}
                      sx={{ bgcolor: `${TYPE_META[sug.type]?.color || '#9e9e9e'}22`, color: TYPE_META[sug.type]?.color || '#9e9e9e', border: `1px solid ${TYPE_META[sug.type]?.color || '#9e9e9e'}44`, cursor: 'pointer', '&:hover': { opacity: 0.8 } }}
                    />
                  ))}
                </Box>
              </Box>
            )}
          </>
        )}

        {/* Generate Itinerary */}
        {segments.length > 0 && (
          <>
            <Divider sx={{ my: 2 }} />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', mb: 2 }}>
              <Button variant="contained" size="large" startIcon={generating ? <CircularProgress size={18} color="inherit" /> : <AutoAwesomeIcon />}
                onClick={onGenerateItinerary} disabled={generating}
                sx={{ background: 'linear-gradient(135deg, #00897b 0%, #00acc1 100%)', px: 4 }}>
                {generating ? 'Claude is planning your trip…' : 'Generate Itinerary'}
              </Button>
              <Typography variant="caption" color="text.secondary">
                {totalItems > 0
                  ? `${totalItems} idea${totalItems !== 1 ? 's' : ''} across ${segments.length} segment${segments.length !== 1 ? 's' : ''}`
                  : 'Add ideas above or let Claude plan from scratch'}
              </Typography>
            </Box>
          </>
        )}
      </Box>
    </Box>

      {/* Segment menu */}
      <Menu anchorEl={segMenuAnchor} open={Boolean(segMenuAnchor)} onClose={() => setSegMenuAnchor(null)}>
        <MenuItem onClick={() => { setEditSeg(segMenuTarget); setSegMenuAnchor(null); }}>
          <EditIcon fontSize="small" sx={{ mr: 1 }} /> Edit
        </MenuItem>
        <MenuItem onClick={() => handleDeleteSegment(segMenuTarget?.id)} sx={{ color: 'error.main' }}>
          <DeleteIcon fontSize="small" sx={{ mr: 1 }} /> Delete
        </MenuItem>
      </Menu>

      {/* Item menu */}
      <Menu anchorEl={itemMenuAnchor} open={Boolean(itemMenuAnchor)} onClose={() => setItemMenuAnchor(null)}>
        <MenuItem onClick={() => { setEditItem(itemMenuTarget); setAddItemOpen(true); setItemMenuAnchor(null); }}>
          <EditIcon fontSize="small" sx={{ mr: 1 }} /> Edit
        </MenuItem>
        <MenuItem onClick={() => handleDeleteItem(itemMenuTarget?.id)} sx={{ color: 'error.main' }}>
          <DeleteIcon fontSize="small" sx={{ mr: 1 }} /> Delete
        </MenuItem>
      </Menu>

      {/* Dialogs */}
      <AddSegmentDialog open={addSegOpen} onClose={() => setAddSegOpen(false)} onSave={handleAddSegment} />
      <AddSegmentDialog open={Boolean(editSeg)} onClose={() => setEditSeg(null)} onSave={handleEditSegment} existing={editSeg} />
      <AddItemDialog open={addItemOpen} onClose={() => { setAddItemOpen(false); setEditItem(null); }}
        onSave={editItem ? handleEditItem : handleAddItem} existing={editItem}
        autocompleteService={autocompleteService} placesService={placesService} />
    </Box>
  );
};

// ─── ItineraryView ────────────────────────────────────────────────────────────

const ItineraryView = ({ itinerary, onUpdateItinerary, isLoaded, mapRef }) => {
  const [selectedDay, setSelectedDay] = useState(1);
  const [selectedMarker, setSelectedMarker] = useState(null); // { dayNum, actIdx }
  const [mapMode, setMapMode] = useState(() => window.innerWidth < 600 ? 'list' : 'split');
  const [editingActivity, setEditingActivity] = useState(null); // { dayIdx, actIdx }
  const [editTime, setEditTime] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const mapInstance = useRef(null);

  const days = itinerary?.days || [];
  const currentDay = days.find(d => d.dayNumber === selectedDay);
  const currentColor = dayColor(selectedDay);

  // Fit map to selected day's markers
  useEffect(() => {
    if (!mapInstance.current || !currentDay) return;
    const points = (currentDay.activities || []).filter(a => a.location).map(a => a.location);
    if (points.length === 0) return;
    if (points.length === 1) {
      mapInstance.current.panTo(points[0]);
      mapInstance.current.setZoom(15);
    } else {
      const bounds = new window.google.maps.LatLngBounds();
      points.forEach(p => bounds.extend(p));
      mapInstance.current.fitBounds(bounds, 60);
    }
  }, [selectedDay, currentDay]);

  const onMapLoad = useCallback((map) => {
    mapInstance.current = map;
    if (mapRef) mapRef.current = map;
    // Fit all pins on initial load
    const bounds = new window.google.maps.LatLngBounds();
    let any = false;
    days.forEach(day => day.activities?.forEach(a => { if (a.location) { bounds.extend(a.location); any = true; } }));
    if (any) map.fitBounds(bounds, 60);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const moveActivity = (dayIdx, actIdx, dir) => {
    const newDays = days.map((d, di) => {
      if (di !== dayIdx) return d;
      const acts = [...d.activities];
      const target = actIdx + dir;
      if (target < 0 || target >= acts.length) return d;
      [acts[actIdx], acts[target]] = [acts[target], acts[actIdx]];
      return { ...d, activities: acts };
    });
    onUpdateItinerary({ ...itinerary, days: newDays });
  };

  const deleteActivity = (dayIdx, actIdx) => {
    const newDays = days.map((d, di) => {
      if (di !== dayIdx) return d;
      return { ...d, activities: d.activities.filter((_, ai) => ai !== actIdx) };
    });
    onUpdateItinerary({ ...itinerary, days: newDays });
  };

  const saveActivityEdit = () => {
    if (!editingActivity) return;
    const { dayIdx, actIdx } = editingActivity;
    const newDays = days.map((d, di) => {
      if (di !== dayIdx) return d;
      const acts = d.activities.map((a, ai) => ai === actIdx ? { ...a, time: editTime, notes: editNotes } : a);
      return { ...d, activities: acts };
    });
    onUpdateItinerary({ ...itinerary, days: newDays });
    setEditingActivity(null);
  };

  // Build polyline path for selected day
  const polylinePath = (currentDay?.activities || []).filter(a => a.location).map(a => a.location);

  const DayList = () => (
    <Box sx={{ overflow: 'auto', height: '100%', pr: 1 }}>
      {days.map((day, dayIdx) => {
        const color = dayColor(day.dayNumber);
        const isSelected = day.dayNumber === selectedDay;
        return (
          <Paper key={day.dayNumber} elevation={0} onClick={() => setSelectedDay(day.dayNumber)}
            sx={{ mb: 2, borderRadius: 2, overflow: 'hidden', cursor: 'pointer', border: `2px solid ${isSelected ? color : 'transparent'}`, bgcolor: 'rgba(255,255,255,0.04)', '&:hover': { bgcolor: 'rgba(255,255,255,0.07)' } }}>
            <Box sx={{ px: 2, py: 1, bgcolor: `${color}22`, borderBottom: `1px solid ${color}44`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box>
                <Typography variant="subtitle2" fontWeight={700} sx={{ color }}>
                  Day {day.dayNumber}{day.date ? ` — ${day.date}` : ''}
                </Typography>
                <Typography variant="caption" color="text.secondary">{day.label}</Typography>
              </Box>
              <Chip label={day.segmentName} size="small" sx={{ bgcolor: `${color}33`, color }} />
            </Box>
            <Box sx={{ px: 2, py: 1 }}>
              {(day.activities || []).map((act, actIdx) => {
                const isEditing = editingActivity?.dayIdx === dayIdx && editingActivity?.actIdx === actIdx;
                return (
                  <Box key={act.id || actIdx}
                    sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, py: 0.75, borderBottom: actIdx < day.activities.length - 1 ? '1px solid rgba(255,255,255,0.06)' : 'none' }}
                    onClick={e => e.stopPropagation()}>
                    <TypeIcon type={act.type} size={16} />
                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                      {isEditing ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <TextField size="small" label="Time" type="time" value={editTime}
                              onChange={e => setEditTime(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ width: 120 }} />
                            <Button size="small" variant="contained" onClick={saveActivityEdit}>Save</Button>
                            <Button size="small" onClick={() => setEditingActivity(null)}>Cancel</Button>
                          </Box>
                          <TextField size="small" label="Notes" value={editNotes} onChange={e => setEditNotes(e.target.value)} fullWidth multiline maxRows={3} />
                        </Box>
                      ) : (
                        <>
                          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                            {act.time && (
                              <Typography variant="caption" sx={{ color, fontWeight: 600, minWidth: 40 }}>{act.time}</Typography>
                            )}
                            <Typography variant="body2" noWrap>{act.name}</Typography>
                            {act.location && <PlaceIcon sx={{ fontSize: 12, color: 'text.disabled' }} />}
                          </Box>
                          {act.notes && <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.2 }}>{act.notes}</Typography>}
                        </>
                      )}
                    </Box>
                    {!isEditing && (
                      <Box sx={{ display: 'flex', gap: 0 }}>
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => { setEditingActivity({ dayIdx, actIdx }); setEditTime(act.time || ''); setEditNotes(act.notes || ''); }}>
                            <EditIcon sx={{ fontSize: 14 }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Move up">
                          <span><IconButton size="small" onClick={() => moveActivity(dayIdx, actIdx, -1)} disabled={actIdx === 0}>
                            <ArrowUpwardIcon sx={{ fontSize: 14 }} />
                          </IconButton></span>
                        </Tooltip>
                        <Tooltip title="Move down">
                          <span><IconButton size="small" onClick={() => moveActivity(dayIdx, actIdx, 1)} disabled={actIdx === day.activities.length - 1}>
                            <ArrowDownwardIcon sx={{ fontSize: 14 }} />
                          </IconButton></span>
                        </Tooltip>
                        <Tooltip title="Remove">
                          <IconButton size="small" onClick={() => deleteActivity(dayIdx, actIdx)} sx={{ color: 'error.main' }}>
                            <DeleteIcon sx={{ fontSize: 14 }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    )}
                  </Box>
                );
              })}
            </Box>
          </Paper>
        );
      })}
    </Box>
  );

  const MapPanel = () => (
    isLoaded ? (
      <GoogleMap mapContainerStyle={{ width: '100%', height: '100%', borderRadius: 12 }}
        zoom={12} center={{ lat: 0, lng: 0 }} onLoad={onMapLoad}
        options={{ mapTypeControl: false, streetViewControl: false, styles: [{ elementType: 'geometry', stylers: [{ color: '#212121' }] }, { elementType: 'labels.text.fill', stylers: [{ color: '#757575' }] }, { elementType: 'labels.text.stroke', stylers: [{ color: '#212121' }] }, { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#000000' }] }] }}>
        {/* All markers */}
        {days.flatMap(day =>
          (day.activities || []).map((act, actIdx) => {
            if (!act.location) return null;
            const color = dayColor(day.dayNumber);
            const isSelected = day.dayNumber === selectedDay;
            return (
              <MarkerF key={`${day.dayNumber}-${actIdx}`}
                position={act.location}
                icon={{ url: pinSvg(color, actIdx + 1), scaledSize: new window.google.maps.Size(isSelected ? 36 : 26, isSelected ? 46 : 33), anchor: new window.google.maps.Point(isSelected ? 18 : 13, isSelected ? 46 : 33) }}
                onClick={() => { setSelectedMarker({ dayNum: day.dayNumber, actIdx }); setSelectedDay(day.dayNumber); }}
              />
            );
          })
        )}
        {/* Polyline for selected day */}
        {polylinePath.length > 1 && (
          <PolylineF path={polylinePath} options={{ strokeColor: currentColor, strokeOpacity: 0.8, strokeWeight: 3, geodesic: true, icons: [{ icon: { path: window.google.maps.SymbolPath.FORWARD_CLOSED_ARROW }, offset: '50%' }] }} />
        )}
        {/* InfoWindow */}
        {selectedMarker && (() => {
          const day = days.find(d => d.dayNumber === selectedMarker.dayNum);
          const act = day?.activities?.[selectedMarker.actIdx];
          if (!act?.location) return null;
          return (
            <InfoWindowF position={act.location} onCloseClick={() => setSelectedMarker(null)}>
              <Box sx={{ color: '#000', maxWidth: 200 }}>
                <Typography variant="subtitle2" fontWeight={700}>{act.name}</Typography>
                {act.time && <Typography variant="caption">🕐 {act.time}</Typography>}
                {act.notes && <Typography variant="body2" sx={{ mt: 0.5 }}>{act.notes}</Typography>}
                {act.address && <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>{act.address}</Typography>}
              </Box>
            </InfoWindowF>
          );
        })()}
      </GoogleMap>
    ) : (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <CircularProgress />
      </Box>
    )
  );

  return (
    <Box>
      {/* Day color legend */}
      <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        {days.map(day => (
          <Chip key={day.dayNumber} label={`Day ${day.dayNumber}${day.date ? ` (${day.date})` : ''}`}
            size="small" onClick={() => setSelectedDay(day.dayNumber)}
            sx={{ bgcolor: `${dayColor(day.dayNumber)}${day.dayNumber === selectedDay ? 'cc' : '33'}`, color: dayColor(day.dayNumber), border: `1px solid ${dayColor(day.dayNumber)}66`, fontWeight: day.dayNumber === selectedDay ? 700 : 400 }}
          />
        ))}
        <Box sx={{ flexGrow: 1 }} />
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          {[{ v: 'split', I: <Box sx={{ display: 'flex', gap: '2px' }}><ListAltIcon sx={{ fontSize: 16 }} /><MapIcon sx={{ fontSize: 16 }} /></Box> }, { v: 'list', I: <ListAltIcon /> }, { v: 'map', I: <MapIcon /> }].map(({ v, I }) => (
            <Tooltip key={v} title={v}><IconButton size="small" onClick={() => setMapMode(v)} sx={{ color: mapMode === v ? 'primary.main' : 'text.secondary' }}>{I}</IconButton></Tooltip>
          ))}
        </Box>
      </Box>

      {/* Content */}
      {mapMode === 'split' && (
        <Box sx={{ display: 'flex', gap: 2, height: 600 }}>
          <Box sx={{ width: '40%', flexShrink: 0 }}><DayList /></Box>
          <Box sx={{ flexGrow: 1 }}><MapPanel /></Box>
        </Box>
      )}
      {mapMode === 'list' && <Box sx={{ maxHeight: 600, overflow: 'auto' }}><DayList /></Box>}
      {mapMode === 'map' && <Box sx={{ height: 600 }}><MapPanel /></Box>}
    </Box>
  );
};

// ─── JournalView ──────────────────────────────────────────────────────────────

const JournalView = ({ itinerary, onUpdateItinerary, listId }) => {
  const [uploadingFor, setUploadingFor] = useState(null); // activityId being uploaded
  const fileInputRef = useRef(null);
  const days = itinerary?.days || [];

  const updateActivity = (dayIdx, actIdx, updates) => {
    const newDays = days.map((d, di) => {
      if (di !== dayIdx) return d;
      return { ...d, activities: d.activities.map((a, ai) => ai === actIdx ? { ...a, ...updates } : a) };
    });
    onUpdateItinerary({ ...itinerary, days: newDays });
  };

  const handlePhotoUpload = async (dayIdx, actIdx, act, files) => {
    const file = files[0];
    if (!file) return;
    setUploadingFor(act.id);
    try {
      const storage = getStorage();
      const user = auth.currentUser;
      if (!user) throw new Error('Not authenticated');
      const path = `travel-photos/${user.uid}/${listId}/${act.id || uid()}/${Date.now()}_${file.name}`;
      const ref = storageRef(storage, path);
      const snap = await uploadBytes(ref, file);
      const url = await getDownloadURL(snap.ref);
      updateActivity(dayIdx, actIdx, { photoUrls: [...(act.photoUrls || []), url] });
    } catch (e) {
      alert('Photo upload failed: ' + e.message);
    } finally {
      setUploadingFor(null);
    }
  };

  const addExpense = (dayIdx, actIdx, act) => {
    const expense = { id: uid(), description: '', amount: '', currency: 'SGD' };
    updateActivity(dayIdx, actIdx, { expenses: [...(act.expenses || []), expense] });
  };

  const updateExpense = (dayIdx, actIdx, act, expId, field, value) => {
    const expenses = act.expenses.map(e => e.id === expId ? { ...e, [field]: value } : e);
    updateActivity(dayIdx, actIdx, { expenses });
  };

  const deleteExpense = (dayIdx, actIdx, act, expId) => {
    updateActivity(dayIdx, actIdx, { expenses: act.expenses.filter(e => e.id !== expId) });
  };

  // Expense summary
  const expSummary = {};
  days.forEach(day => (day.activities || []).forEach(act => {
    (act.expenses || []).forEach(e => {
      if (!e.amount || isNaN(parseFloat(e.amount))) return;
      const cur = e.currency || 'SGD';
      expSummary[cur] = (expSummary[cur] || 0) + parseFloat(e.amount);
    });
  }));

  return (
    <Box>
      {days.map((day, dayIdx) => {
        const color = dayColor(day.dayNumber);
        const completedCount = (day.activities || []).filter(a => a.completed).length;
        return (
          <Accordion key={day.dayNumber} defaultExpanded={day.dayNumber === 1} elevation={0}
            sx={{ mb: 2, bgcolor: 'rgba(255,255,255,0.04)', borderRadius: '8px !important', '&:before': { display: 'none' } }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%', pr: 1 }}>
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: color, flexShrink: 0 }} />
                <Box sx={{ flexGrow: 1 }}>
                  <Typography variant="subtitle1" fontWeight={600}>Day {day.dayNumber}{day.date ? ` — ${day.date}` : ''}</Typography>
                  <Typography variant="caption" color="text.secondary">{day.label}</Typography>
                </Box>
                <Chip label={`${completedCount}/${day.activities?.length || 0} done`} size="small"
                  sx={{ bgcolor: completedCount === day.activities?.length ? '#66bb6a33' : 'rgba(255,255,255,0.06)', color: completedCount === day.activities?.length ? '#66bb6a' : 'text.secondary' }} />
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0 }}>
              {(day.activities || []).map((act, actIdx) => (
                <Paper key={act.id || actIdx} elevation={0} sx={{ mb: 2, p: 2, bgcolor: act.completed ? 'rgba(102,187,106,0.06)' : 'rgba(255,255,255,0.03)', borderRadius: 2, border: `1px solid ${act.completed ? 'rgba(102,187,106,0.2)' : 'rgba(255,255,255,0.06)'}` }}>
                  {/* Header */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                    <IconButton size="small" onClick={() => updateActivity(dayIdx, actIdx, { completed: !act.completed })} sx={{ color: act.completed ? '#66bb6a' : 'text.disabled' }}>
                      {act.completed ? <CheckCircleIcon /> : <RadioButtonUncheckedIcon />}
                    </IconButton>
                    <TypeIcon type={act.type} />
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="body1" fontWeight={600} sx={{ textDecoration: act.completed ? 'line-through' : 'none' }}>
                        {act.name}
                      </Typography>
                      {act.time && <Typography variant="caption" color="text.secondary">🕐 {act.time}</Typography>}
                    </Box>
                    <TypeChip type={act.type} />
                  </Box>

                  {/* Rating */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ minWidth: 50 }}>Rating:</Typography>
                    <Rating value={act.rating || 0} onChange={(_, v) => updateActivity(dayIdx, actIdx, { rating: v })} size="small" />
                  </Box>

                  {/* Review */}
                  <TextField size="small" fullWidth multiline rows={2} placeholder="Write a review or notes about this activity..."
                    value={act.review || ''} onChange={e => updateActivity(dayIdx, actIdx, { review: e.target.value })}
                    sx={{ mb: 1.5 }} />

                  {/* Expenses */}
                  <Box sx={{ mb: 1.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                      <AttachMoneyIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                      <Typography variant="caption" color="text.secondary" fontWeight={600}>EXPENSES</Typography>
                      <Button size="small" startIcon={<AddIcon />} onClick={() => addExpense(dayIdx, actIdx, act)} sx={{ ml: 'auto', fontSize: '0.7rem' }}>Add</Button>
                    </Box>
                    {(act.expenses || []).map(exp => (
                      <Box key={exp.id} sx={{ display: 'flex', gap: 1, mb: 0.5, alignItems: 'center' }}>
                        <TextField size="small" placeholder="Description" value={exp.description}
                          onChange={e => updateExpense(dayIdx, actIdx, act, exp.id, 'description', e.target.value)}
                          sx={{ flexGrow: 1 }} />
                        <TextField size="small" placeholder="Amount" type="number" value={exp.amount}
                          onChange={e => updateExpense(dayIdx, actIdx, act, exp.id, 'amount', e.target.value)}
                          sx={{ width: 100 }} />
                        <Select size="small" value={exp.currency || 'SGD'}
                          onChange={e => updateExpense(dayIdx, actIdx, act, exp.id, 'currency', e.target.value)}
                          sx={{ width: 90 }}>
                          {CURRENCIES.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                        </Select>
                        <IconButton size="small" onClick={() => deleteExpense(dayIdx, actIdx, act, exp.id)} sx={{ color: 'error.main' }}>
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    ))}
                  </Box>

                  {/* Photos */}
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                      <AddPhotoAlternateIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                      <Typography variant="caption" color="text.secondary" fontWeight={600}>PHOTOS</Typography>
                      <Button size="small" startIcon={uploadingFor === act.id ? <CircularProgress size={12} /> : <AddIcon />}
                        disabled={uploadingFor === act.id}
                        onClick={() => { fileInputRef.current.dataset.dayIdx = dayIdx; fileInputRef.current.dataset.actIdx = actIdx; fileInputRef.current.dataset.actId = act.id; fileInputRef.current.click(); }}
                        sx={{ ml: 'auto', fontSize: '0.7rem' }}>
                        {uploadingFor === act.id ? 'Uploading…' : 'Add Photo'}
                      </Button>
                    </Box>
                    {(act.photoUrls || []).length > 0 && (
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        {(act.photoUrls || []).map((url, pi) => (
                          <Box key={pi} sx={{ position: 'relative' }}>
                            <img src={url} alt="" style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 8 }} />
                            <IconButton size="small"
                              onClick={() => updateActivity(dayIdx, actIdx, { photoUrls: act.photoUrls.filter((_, i) => i !== pi) })}
                              sx={{ position: 'absolute', top: -6, right: -6, bgcolor: 'rgba(0,0,0,0.7)', color: 'white', p: '2px', '&:hover': { bgcolor: 'error.main' } }}>
                              <CloseIcon sx={{ fontSize: 12 }} />
                            </IconButton>
                          </Box>
                        ))}
                      </Box>
                    )}
                  </Box>
                </Paper>
              ))}
            </AccordionDetails>
          </Accordion>
        );
      })}

      {/* Expense summary */}
      {Object.keys(expSummary).length > 0 && (
        <Paper elevation={0} sx={{ p: 2, bgcolor: 'rgba(255,193,7,0.08)', border: '1px solid rgba(255,193,7,0.2)', borderRadius: 2 }}>
          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
            <AttachMoneyIcon fontSize="small" /> Total Trip Expenses
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            {Object.entries(expSummary).map(([cur, amt]) => (
              <Chip key={cur} label={`${cur} ${amt.toFixed(2)}`} size="small"
                sx={{ bgcolor: 'rgba(255,193,7,0.15)', color: '#ffc107', fontWeight: 600 }} />
            ))}
          </Box>
        </Paper>
      )}

      {/* Hidden file input for photo upload */}
      <input type="file" accept="image/*" ref={fileInputRef} style={{ display: 'none' }}
        onChange={e => {
          const di = parseInt(fileInputRef.current.dataset.dayIdx);
          const ai = parseInt(fileInputRef.current.dataset.actIdx);
          const day = days[di];
          const act = day?.activities?.[ai];
          if (act) handlePhotoUpload(di, ai, act, e.target.files);
          e.target.value = '';
        }} />
    </Box>
  );
};

// ─── Main TravelList ──────────────────────────────────────────────────────────

const TravelList = ({ list, onUpdateList }) => {
  const [segments, setSegments] = useState(list.segments || []);
  const [itinerary, setItinerary] = useState(list.itinerary || null);
  const [view, setView] = useState('plan');
  const [selectedSegmentId, setSelectedSegmentId] = useState(() => (list.segments || [])[0]?.id || null);
  const [generating, setGenerating] = useState(false);
  const [genError, setGenError] = useState('');

  const autocompleteService = useRef(null);
  const placesService = useRef(null);
  const psDiv = useRef(null); // invisible div for PlacesService
  const mapRef = useRef(null);

  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries: LIBRARIES,
  });

  useEffect(() => {
    if (!isLoaded) return;
    autocompleteService.current = new window.google.maps.places.AutocompleteService();
    if (psDiv.current) {
      placesService.current = new window.google.maps.places.PlacesService(psDiv.current);
    }
  }, [isLoaded]);

  // Keep local state in sync with Firestore updates
  useEffect(() => { setSegments(list.segments || []); }, [list.segments]);
  useEffect(() => { setItinerary(list.itinerary || null); }, [list.itinerary]);

  // Auto-create one segment named after the trip on first load
  useEffect(() => {
    if ((list.segments || []).length === 0) {
      const seg = { id: uid(), name: list.name, startDate: '', endDate: '', flightDetails: '', hotelDetails: '', notes: '', brainstorm: [] };
      onUpdateList(list.id, { segments: [seg] });
      setSegments([seg]);
      setSelectedSegmentId(seg.id);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const save = useCallback((updates) => {
    onUpdateList(list.id, updates);
  }, [list.id, onUpdateList]);

  const handleUpdateSegments = useCallback((newSegments) => {
    setSegments(newSegments);
    save({ segments: newSegments });
  }, [save]);

  const handleUpdateItinerary = useCallback((newItinerary) => {
    setItinerary(newItinerary);
    save({ itinerary: newItinerary });
  }, [save]);

  const handleGenerateItinerary = async () => {
    if (itinerary && !window.confirm('Regenerate itinerary? Your current journal entries will be lost.')) return;
    setGenerating(true);
    setGenError('');
    try {
      const res = await fetch('/api/ai/generate-itinerary', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tripName: list.name, segments }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to generate');

      // Build a lookup map: brainstormItemId → { location, placeId, address }
      const itemMap = {};
      segments.forEach(seg => (seg.brainstorm || []).forEach(item => { itemMap[item.id] = item; }));

      const enrichedDays = (data.days || []).map(day => ({
        ...day,
        activities: (day.activities || []).map(act => {
          const bItem = act.brainstormItemId ? itemMap[act.brainstormItemId] : null;
          return {
            ...act,
            id: act.id || uid(),
            location: bItem?.location || act.location || null,
            placeId: bItem?.placeId || act.placeId || null,
            address: bItem?.address || act.address || null,
            completed: false,
            rating: null,
            review: '',
            expenses: [],
            photoUrls: [],
          };
        }),
      }));

      const newItinerary = { generatedAt: new Date().toISOString(), days: enrichedDays };
      handleUpdateItinerary(newItinerary);
      setView('itinerary');
    } catch (e) {
      setGenError(e.message);
    } finally {
      setGenerating(false);
    }
  };

  return (
    <Box>
      {/* Invisible div for PlacesService initialization */}
      <div ref={psDiv} style={{ display: 'none' }} />

      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={700}>{list.name}</Typography>
          <Typography variant="body2" color="text.secondary">
            {segments.length} segment{segments.length !== 1 ? 's' : ''} ·{' '}
            {segments.reduce((a, s) => a + (s.brainstorm?.length || 0), 0)} ideas
            {itinerary ? ` · ${itinerary.days?.length || 0}-day itinerary` : ''}
          </Typography>
        </Box>
        {itinerary && (
          <Tooltip title="Regenerate itinerary with Claude">
            <Button size="small" startIcon={<RefreshIcon />} onClick={() => setView('plan')}>
              Edit Plan
            </Button>
          </Tooltip>
        )}
      </Box>

      {/* Tabs */}
      <Tabs value={view} onChange={(_, v) => setView(v)} sx={{ mb: 3, borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
        <Tab value="plan" label="Plan" icon={<ListAltIcon />} iconPosition="start" />
        <Tab value="itinerary" label="Itinerary" icon={<MapIcon />} iconPosition="start" disabled={!itinerary} />
        <Tab value="journal" label="Journal" icon={<AttachMoneyIcon />} iconPosition="start" disabled={!itinerary} />
      </Tabs>

      {genError && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setGenError('')}>{genError}</Alert>}
      {loadError && <Alert severity="warning" sx={{ mb: 2 }}>Google Maps failed to load — place search unavailable.</Alert>}

      {view === 'plan' && (
        <PlanView
          list={list}
          segments={segments}
          selectedSegmentId={selectedSegmentId}
          onSelectSegment={setSelectedSegmentId}
          onUpdateSegments={handleUpdateSegments}
          onGenerateItinerary={handleGenerateItinerary}
          generating={generating}
          autocompleteService={autocompleteService}
          placesService={placesService}
        />
      )}
      {view === 'itinerary' && itinerary && (
        <ItineraryView
          itinerary={itinerary}
          onUpdateItinerary={handleUpdateItinerary}
          isLoaded={isLoaded}
          mapRef={mapRef}
        />
      )}
      {view === 'journal' && itinerary && (
        <JournalView
          itinerary={itinerary}
          onUpdateItinerary={handleUpdateItinerary}
          listId={list.id}
        />
      )}
    </Box>
  );
};

export default TravelList;
